/**
 * lib/positions.js · v2.7.45+E13 (Feb-2026)
 * ═════════════════════════════════════════════════════════════════════
 * Mirrors backend/common/positions.py — the canonical 46-position catalog
 * plus the RBAC derivation helpers. Kept in sync manually (small file,
 * rarely changes).
 */

export const POSITION_CATALOG = [
  // Field operations · drivers
  "Taxi Driver I",
  "Taxi Driver II",
  "Taxi Driver III",
  "Senior Taxi Driver I",
  "Senior Taxi Driver II",
  "Senior Taxi Driver III",
  // Field operations · leadership
  "Team Lead I",
  "Team Lead II",
  "Field Supervisor I",
  "Senior Field Supervisor",
  // Dispatch
  "Dispatcher I",
  "Dispatcher II",
  "Dispatcher III",
  "Senior Dispatcher",
  "Dispatcher Supervisor",
  "Dispatch Director",
  // Engineering / IT
  "IT Manager I",
  "IT Manager II",
  "Software Developer I",
  "Software Developer II",
  "Software Developer III",
  "Senior Software Engineer III",
  // Support
  "Direct Support I",
  "Direct Support II",
  "Customer Support",
  "Customer Support Lead",
  "Customer Support Supervisor",
  // Business / sales / PR
  "Business Sales Executive",
  "Government Bidding Specialist I",
  "Government Bidding Specialist II",
  "Public Relations Manager",
  "Public Relations Director",
  // Safety / training
  "Safety Coordinator I",
  "Safety Coordinator II",
  "Safety & Training Manager",
  "Director of Safety",
  // Marketing
  "Director of Marketing",
  // C-suite + executive
  "Chief Financial Officer",
  "Chief Legal Officer",
  "Chief Technology Officer",
  "Chief Peoples Officer",
  "Chief Marketing Officer",
  "Chief Training & Safety Officer",
  "Chief Operating Officer",
  "Chief Executive Officer",
  "Vice President",
  "President",
];

// Position groups for the multi-select UI (collapsible sections).
export const POSITION_GROUPS = [
  { label: "Drivers",            positions: POSITION_CATALOG.slice(0, 6) },
  { label: "Team Leads",         positions: POSITION_CATALOG.slice(6, 8) },
  { label: "Field Supervisors",  positions: POSITION_CATALOG.slice(8, 10) },
  { label: "Dispatch",           positions: POSITION_CATALOG.slice(10, 16) },
  { label: "Engineering / IT",   positions: POSITION_CATALOG.slice(16, 22) },
  { label: "Support",            positions: POSITION_CATALOG.slice(22, 27) },
  { label: "Business / Sales / PR", positions: POSITION_CATALOG.slice(27, 32) },
  { label: "Safety / Training",  positions: POSITION_CATALOG.slice(32, 36) },
  { label: "Marketing",          positions: POSITION_CATALOG.slice(36, 37) },
  { label: "Executive · C-Suite", positions: POSITION_CATALOG.slice(37, 44) },
  { label: "Executive · VP/President", positions: POSITION_CATALOG.slice(44, 46) },
];

export const POSITION_TO_TIER = {
  "Taxi Driver I": "driver",
  "Taxi Driver II": "driver",
  "Taxi Driver III": "driver",
  "Senior Taxi Driver I": "driver",
  "Senior Taxi Driver II": "driver",
  "Senior Taxi Driver III": "driver",
  "Team Lead I": "team_leader",
  "Team Lead II": "team_leader",
  "Field Supervisor I": "supervisor",
  "Senior Field Supervisor": "supervisor",
  "Dispatcher I": "dispatcher",
  "Dispatcher II": "dispatcher",
  "Dispatcher III": "dispatcher",
  "Senior Dispatcher": "dispatcher",
  "Dispatcher Supervisor": "supervisor",
  "Dispatch Director": "operating_partner",
  "IT Manager I": "developer",
  "IT Manager II": "developer",
  "Software Developer I": "developer",
  "Software Developer II": "developer",
  "Software Developer III": "developer",
  "Senior Software Engineer III": "developer",
  "Direct Support I": "driver",
  "Direct Support II": "driver",
  "Customer Support": "driver",
  "Customer Support Lead": "team_leader",
  "Customer Support Supervisor": "supervisor",
  "Business Sales Executive": "operating_partner",
  "Government Bidding Specialist I": "operating_partner",
  "Government Bidding Specialist II": "operating_partner",
  "Public Relations Manager": "operating_partner",
  "Public Relations Director": "operating_partner",
  "Safety Coordinator I": "team_leader",
  "Safety Coordinator II": "supervisor",
  "Safety & Training Manager": "operating_partner",
  "Director of Safety": "operating_partner",
  "Director of Marketing": "operating_partner",
  "Chief Financial Officer": "operating_partner",
  "Chief Legal Officer": "operating_partner",
  "Chief Technology Officer": "developer",
  "Chief Peoples Officer": "operating_partner",
  "Chief Marketing Officer": "operating_partner",
  "Chief Training & Safety Officer": "operating_partner",
  "Chief Operating Officer": "operating_partner",
  "Chief Executive Officer": "admin",
  "Vice President": "admin",
  "President": "admin",
};

const TIER_RANK = {
  driver: 10,
  team_leader: 20,
  dispatcher: 30,
  supervisor: 40,
  operating_partner: 50,
  developer: 60,
  // v2.7.58+E42 · General Partner sits ABOVE Admin — it's the supreme
  // super-user that bypasses every permission gate.
  admin: 70,
  general_partner: 80,
};

export function deriveCanonicalRole(positions = [], fallback = "driver") {
  if (!positions?.length) return fallback;
  let best = fallback;
  let bestRank = -1;
  for (const p of positions) {
    const tier = POSITION_TO_TIER[p];
    if (tier && TIER_RANK[tier] > bestRank) {
      best = tier;
      bestRank = TIER_RANK[tier];
    }
  }
  return best;
}

// v2.7.58+E42 · General Partner is a first-class super-editor. Anyone
// with this role can flip positions on any account.
export const POSITION_EDITORS = new Set(["admin", "operating_partner", "developer", "general_partner"]);

export function canEditPositions(canonicalRole) {
  return POSITION_EDITORS.has((canonicalRole || "").toLowerCase());
}

export function canManageDeveloperConsole(canonicalRole) {
  // v2.7.45+E15 · Operating Partner now has full parity with Developer
  // (granted by the executive leadership alignment update). Both tiers
  // — plus the master admin — can access the renamed "Email Admin"
  // console; everyone else (supervisor, dispatcher, team_leader,
  // driver) remains locked out.
  // v2.7.58+E42 · General Partner is the supreme super-user and always
  // gets in.
  // v2.7.65+E189 · Platform Owner (Derick @ derick@bullertaxi.com) is
  // the supreme in-app role and MUST always pass every access gate.
  const role = (canonicalRole || "").toLowerCase();
  return role === "developer" || role === "admin" || role === "operating_partner" || role === "general_partner" || role === "platform_owner";
}

// v2.7.58+E42 · General-Partner-aware supreme check. Any caller with
// this role (or the master env-admin) bypasses every UI gate.
// v2.7.65+E189 · Platform Owner also passes as supreme.
export function isSupremeRole(canonicalRole) {
  const r = (canonicalRole || "").toLowerCase();
  return r === "general_partner" || r === "admin" || r === "platform_owner";
}
